import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";

import { fmtMoney, fmtDate, partyLabel } from "@/lib/format";
export default function VendorLedger() {
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState("");
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/procurement/vendors").then(({ data }) => setVendors(data)).catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  useEffect(() => {
    if (!vendorId) { setLedger(null); return; }
    setLoading(true);
    api.get(`/procurement/vendors/${vendorId}/ledger`)
      .then(({ data }) => setLedger(data))
      .catch((err) => toast.error(getErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [vendorId]);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  return (
    <div className="space-y-6" data-testid="vendor-ledger-page">
      <PageHeader title="Vendor Ledger" description="Outstanding balance and ageing across bills, advances and debit notes for a vendor." />
      <div className="max-w-xs">
        <Label>Vendor</Label>
        <Select value={vendorId} onValueChange={setVendorId}>
          <SelectTrigger data-testid="ledger-vendor-select" className="mt-1.5"><SelectValue placeholder="Select a vendor" /></SelectTrigger>
          <SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {!vendorId ? (
        <EmptyState title="Select a vendor to view their ledger" testId="vendor-ledger-empty-state" />
      ) : loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : ledger ? (
        <>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            <div className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs text-muted-foreground">Outstanding</p>
              <p className="mt-1 font-mono text-lg font-bold text-foreground" data-testid="ledger-outstanding">{ledger.outstanding}</p>
            </div>
            {ledger.aging.map((bucket) => (
              <div key={bucket.label} className="rounded-xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">{bucket.label} days</p>
                <p className="mt-1 font-mono text-lg font-semibold text-foreground">{bucket.amount}</p>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-border bg-card">
            {ledger.entries.length === 0 ? (
              <EmptyState title="No posted documents for this vendor yet" />
            ) : (
              <Table>
                <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Number</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
                <TableBody>
                  {ledger.entries.map((e) => (
                    <TableRow key={e.doc_id} data-testid={`ledger-entry-row-${e.number}`}>
                      <TableCell className="text-muted-foreground">{e.doc_type.replaceAll("_", " ")}</TableCell>
                      <TableCell className="font-mono text-xs text-foreground">{e.number}</TableCell>
                      <TableCell className="text-muted-foreground">{fmtDate(e.doc_date)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-foreground">{fmtMoney(e.debit)}</TableCell>
                      <TableCell className="text-right font-mono text-xs text-foreground">{fmtMoney(e.credit)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </>
      ) : null}
    </div>
  );
}
